import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Flex, Tag, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Input, Select, Text, Stack, Divider } from '@chakra-ui/react';
import { useDebouncyEffect } from 'use-debouncy';
import { useThemeEditor } from '@hypertheme-editor/chakra-ui-core';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var ThemeEditorFontSizesItem = function (props) {
    var _a = props.sampleTitle, sampleTitle = _a === void 0 ? 'Lorem ipsum dolor sit' : _a, size = props.size, value = props.value, onChange = props.onChange;
    var _b = useState(value), currentValue = _b[0], setCurrentValue = _b[1];
    var cssValue = useMemo(function () {
        return window && currentValue
            ? window.CSSStyleValue.parse('font-size', currentValue)
            : undefined;
    }, [currentValue]);
    var handleSliderChange = useCallback(function (newValue) {
        setCurrentValue("" + newValue + cssValue.unit);
    }, [cssValue.unit]);
    var handleInputChange = useCallback(function (event) {
        setCurrentValue("" + event.target.value + cssValue.unit);
    }, [cssValue.unit]);
    var handleUnitChange = useCallback(function (event) {
        if (cssValue.unit === 'px' && (event.target.value === 'em' || event.target.value === 'rem')) {
            setCurrentValue("" + cssValue.value / 16 + event.target.value);
        }
        else if ((cssValue.unit === 'em' || cssValue.unit === 'rem') &&
            event.target.value === 'px') {
            setCurrentValue("" + (cssValue.value * 16).toFixed(0) + event.target.value);
        }
        else {
            setCurrentValue("" + cssValue.value + event.target.value);
        }
    }, [cssValue.unit, cssValue.value]);
    useDebouncyEffect(function () { return onChange({ size: size, value: currentValue }); }, 500, [currentValue]);
    useEffect(function () {
        setCurrentValue(value);
    }, [value]);
    return (React.createElement(Box, { fontSize: "1rem" },
        React.createElement(Flex, { justifyContent: "space-between", mt: "0.5rem", alignItems: "center" },
            React.createElement(Tag, { alignItems: "center", minW: "min-content", fontSize: "0.75rem", px: "0.5rem" }, size),
            React.createElement(Slider, { "aria-label": "slider-ex-1", min: 0, max: cssValue.unit === 'px' ? 100 : 10, step: cssValue.unit === 'px' ? 1 : 0.001, mx: "1rem", value: cssValue.value, onChange: handleSliderChange },
                React.createElement(SliderTrack, null,
                    React.createElement(SliderFilledTrack, { bg: "primary.500" })),
                React.createElement(SliderThumb, null)),
            React.createElement(Flex, { alignItems: "center" },
                React.createElement(Input, { borderRadius: "6px", size: "sm", textAlign: "right", fontSize: "0.875rem", w: "70px", type: "number", value: cssValue.value, onChange: handleInputChange }),
                React.createElement(Select, { size: "sm", w: "70px", ml: "0.5rem", borderRadius: "6px", value: cssValue.unit, onChange: handleUnitChange, fontSize: "0.875rem" },
                    React.createElement("option", { value: "rem" }, "rem"),
                    React.createElement("option", { value: "em" }, "em"),
                    React.createElement("option", { value: "px" }, "px")))),
        React.createElement(Text, { alignItems: "center", minW: "min-content", d: "block", fontSize: currentValue, whiteSpace: "nowrap" }, sampleTitle)));
};

var ThemeEditorFontSizes = function (_a) {
    var _b = _a.scale, scale = _b === void 0 ? ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl', '9xl'] : _b; __rest(_a, ["scale"]);
    var _c = useThemeEditor(), theme = _c.theme, setTheme = _c.setTheme;
    var handleChangeFontSize = useCallback(function (_a) {
        var _b;
        var size = _a.size, value = _a.value;
        if (theme && theme.fontSizes && theme.fontSizes[size] !== value) {
            try {
                var newTheme = __assign(__assign({}, theme), { fontSizes: __assign(__assign({}, theme.fontSizes), (_b = {}, _b[size] = value, _b)) });
                setTheme(newTheme);
            }
            catch (error) {
                //
            }
        }
    }, [setTheme, theme]);
    return (React.createElement(Box, null,
        React.createElement(Flex, { alignItems: "center" },
            React.createElement(Stack, { flex: "1", spacing: "0.75rem", w: "100%", overflow: "scroll", overflowX: "hidden", divider: React.createElement(Divider, null) }, scale.map(function (size) { return (React.createElement(ThemeEditorFontSizesItem, { key: "typography-size-" + size, size: size, value: theme === null || theme === void 0 ? void 0 : theme.fontSizes[size], onChange: handleChangeFontSize })); })))));
};

export { ThemeEditorFontSizes, ThemeEditorFontSizesItem };
//# sourceMappingURL=index.esm.js.map
